// loadGarmentNode — the pure half of the `loadGarment` event-graph node
// (ENG-1108): given the character's garment rows and what the node selected,
// say WHICH row to wear, or WHY there is none.
//
// Deliberately the same shape as loadEnvironmentNode.js, because it is the same
// question asked of a different catalogue, and the two nodes should fail in the
// same words. It lives outside EventRuntime.js for the same reason that one
// does: EventRuntime transitively imports the asset/API layer through
// extensionless specifiers only Vite resolves, so it cannot be loaded under
// plain `node --test`.
//
// The two rules it exists to enforce, both inherited:
//   - A selection that resolves to nothing is an ERROR with a reason, never a
//     plausible substitute. No "first garment in the list" fallback.
//   - The file URL is read off the row the server published, verbatim, in every
//     shape the row arrives in. Nothing here computes a storage path (ENG-999).

/** The garment file URL a row states, across the row shapes this app sees. */
import { GARMENT_OPTION_DEFAULTS } from './garment/garmentImportOptions.js'

export function garmentRowUrl(row) {
  if (!row) return null
  return (
    row.url ??
    row.fileUrl ??
    row.file_url ??
    row.raw?.fileUrl ??
    row.raw?.file_url ??
    null
  )
}

/** The display name a row states (falling back to its id, never to a guess). */
export function garmentRowLabel(row) {
  if (!row) return ''
  return row.name ?? row.raw?.name ?? row.id ?? ''
}

/** The SERVER id of a garment row — registry rows namespace theirs as `garment:<uuid>`. */
export function garmentRowId(row) {
  if (!row) return null
  if (row.rawId) return String(row.rawId)
  const id = row.id ?? row.raw?.id ?? null
  if (!id) return null
  const s = String(id)
  return s.startsWith('garment:') ? s.slice('garment:'.length) : s
}

/**
 * Which garment row the node means.
 *
 * `wired` is the value of the node's `in-garment` data pin when something is
 * wired to it — an id or a name — and it wins over the dropdown, the same
 * "wired overrides dropdown" convention PlayBodyClip / LoadEnvironment follow.
 *
 * Returns `{ none: true }` for an empty selection, `{ row }` on a hit, or
 * `{ error, detail }` where error is one of:
 *   'no-garments' — this character has no garment assets at all
 *   'not-found'   — a selection matching no row (deleted, renamed, or a typo in
 *                   a wired string). `detail` is what was asked for.
 *
 * NONE IS A CHOICE. An empty selection means "wear nothing", which is how a
 * character boots undressed without the author having to find and delete a node
 * — the same lesson the environment node learned the hard way, and it is
 * answered BEFORE the empty-catalogue check because "wear nothing" is a
 * complete answer with no catalogue at all.
 */
export function resolveGarmentSelection(rows, { garmentId, garmentName, wired } = {}) {
  const list = Array.isArray(rows) ? rows : []

  const asked = firstNonEmpty(wired, garmentId, garmentName)
  if (!asked) return { none: true }

  if (list.length === 0) return { error: 'no-garments', detail: null }

  // id first — the stable server identity. Name only after, because two uploads
  // can share a name and a rename must not silently repoint the node.
  const byId = list.find(r => garmentRowId(r) === asked)
  if (byId) return { row: byId }

  const byName = list.find(r => garmentRowLabel(r) === asked)
  if (byName) return { row: byName }

  // A card is named with its extension and an author types it without, or the
  // other way round. Matching the stem is not a fallback to a DIFFERENT
  // garment — it is the same name written two ways.
  const stem = (s) => String(s).replace(/\.(glb|gltf)$/i, '')
  const byStem = list.find(r => stem(garmentRowLabel(r)) === stem(asked))
  if (byStem) return { row: byStem }

  return { error: 'not-found', detail: asked }
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined) continue
    const s = String(v).trim()
    if (s) return s
  }
  return ''
}

/**
 * One sentence saying what went wrong, for the console warning and any UI
 * describing the same failure — kept here so the runtime and the node card
 * cannot drift into two different wordings.
 */
export function describeGarmentError(error, detail) {
  if (error === 'no-garments') {
    return 'this character has no garments — drop a Marvelous .glb onto the content browser and import it as a garment'
  }
  if (error === 'not-found') {
    return `garment "${detail}" not found — it may have been deleted or renamed`
  }
  if (error === 'no-url') {
    return 'garment row has no file URL to load'
  }
  return `garment could not be resolved (${error})`
}

/**
 * The load options a `loadGarment` node states, normalised.
 *
 * These are the same four knobs the `?garment…` URL flags expose, so a node and
 * a deep link can express the same thing. Defaults come from
 * `GARMENT_OPTION_DEFAULTS` rather than being restated here, so the node, the
 * URL path and a garment row's saved options cannot drift apart — they used to
 * be three sets of literals that agreed only by inspection.
 *
 * A node states all three EXPLICITLY, which is why it OVERRIDES whatever the
 * garment row saved: its checkboxes are a decision someone made for this graph
 * (garmentImportOptions.js).
 */
export function garmentNodeOptions(data) {
  const d = data ?? {}
  const D = GARMENT_OPTION_DEFAULTS
  return {
    hideOutfit: typeof d.hideOutfit === 'boolean' ? d.hideOutfit : D.hideOutfit,
    cullSkin: typeof d.cullSkin === 'boolean' ? d.cullSkin : D.cullSkin,
    applyFit: typeof d.applyFit === 'boolean' ? d.applyFit : D.applyFit,
    unit: typeof d.unit === 'string' && d.unit.trim() ? d.unit.trim() : undefined,
  }
}
